export class WebGLAssets {
    static layerIndex: number = 0;
    static allLayersSet = false;

    static setImagesOnStart = false;
    static setVideosOnStart = false;
    static setGpuTextOnStart = false;

    // Pro-Editor mount sets this true BEFORE the first paint (the adapter can't
    // guarantee `props.editorStore` is on the renderer by the time `autoStart`
    // fires its first frame — child-effect ordering leaves a 1–2 frame window in
    // which the video block would hit its loop-mode `video.play()`). While true,
    // the renderer keeps videos paused/static (scrub-only), so a story never
    // starts wall-clock playback in the editor. Reader leaves it false.
    static editorMode = false;

    // Editor timer bridge: when set (by SharedRendererAdapter) the reader's
    // scroll→timer sync routes the scroll-derived timer HERE — into Jan's editor
    // slidePlayer — instead of into the shared player directly. That keeps Jan's
    // editor timer the single master (scroll + timeline + play all write it) and
    // the adapter mirrors it back to the shared renderer, so nothing competes on
    // the shared timer (the flicker was two writers). Null in the reader.
    static onEditorScrollTimer: ((timer: number) => void) | null = null;

    /**
     * PLATEAU-FREIER Zeitwert fuer scroll-getriebene Videos (Jans Fund
     * 04.08.2026: "bleibt exakt auf dem Slide fuer eine kurze Zeit stehen,
     * bevor es dann weiterlaeuft ... gibt es da eine Loesung, dass das Video
     * nicht einrastet").
     *
     * Der normale Zeitgeber ist bewusst NICHT gleichmaessig: er laeuft von
     * `animateAreaFrom` bis `textCenter` und KLEMMT dann fest, solange die
     * Slide zum Lesen stehenbleibt (linearScale mit clamp in VisualStoryStore).
     * Ohne Textbox gibt es diese Ruhezone nicht — deshalb fiel es nur auf
     * Slides mit Text auf, und Loop-Videos waren nie betroffen, weil sie nicht
     * am Zeitgeber haengen.
     *
     * Dieser Wert verteilt dieselbe Zeitspanne stattdessen ueber die GESAMTE
     * Scrollstrecke der Slide, Lesepause eingeschlossen. An den Slide-Grenzen
     * ist er identisch mit dem echten Zeitgeber — dazwischen laeuft er
     * gleichmaessig weiter, statt zu stehen.
     *
     * NUR fuer Scroll-Videos gedacht. Alle anderen Animationen muessen weiter
     * am echten Zeitgeber haengen, sonst waere die Lesepause fuer die ganze
     * Szene weg. `null`, solange keine Scroll-Sicht laeuft (z. B. reines
     * Timeline-Scrubben) — dann faellt der Renderer auf den Zeitgeber zurueck.
     */
    static scrollSmoothTimer: number | null = null;

    // Editor-Blob-Aufloesung (Jans localMediaStore): loest "local-*:<id>"-
    // Referenzen (IndexedDB) zu Objekt-URLs auf. Der Adapter setzt den Hook im
    // Editor; im Reader bleibt er null (veroeffentlichte Scrollys tragen bereits
    // echte URLs). Erster Konsument: environment-hdri (Jans HDRI-Upload).
    static resolveLocalMediaUrl: ((ref: string) => Promise<string | null>) | null = null;

    // Zentrale Medien-Steuerung des Editors (Jans audioControl). Videos MIT TON
    // muessen sich dort an-/abmelden und vor jedem play() fragen, ob Ton gerade
    // global unterdrueckt ist — sonst laeuft der Video-Ton weiter, wenn Jan den
    // Editor verlaesst ("Dashboard"-Button ruft stopAllScrollyAudio, das nur
    // registrierte Elemente erreicht). Im 1.0 importiert Video.ts audioControl
    // direkt; hier laeuft es ueber Hooks, weil audioControl in der App liegt
    // (Paketgrenze, gleiches Muster wie resolveLocalMediaUrl). Reader: null →
    // unveraendertes Verhalten.
    static registerMediaElement: ((el: HTMLMediaElement) => void) | null = null;
    static unregisterMediaElement: ((el: HTMLMediaElement) => void) | null = null;
    static isMediaSuppressed: (() => boolean) | null = null;
    /**
     * STILLE AUFNAHME laeuft (captureRendererFrame): Die Kachel-Aufnahme des
     * Template-Editors spult die Buehne kurz auf die Kachelzeit und zurueck.
     * Ohne dieses Flag wurde ein laufendes Video dabei pausiert, an den
     * Trim-Anfang gesetzt und beim Zuruecksprung neu gestartet — hoerbar als
     * abgehackte Tonfetzen bei jeder Aenderung (Jans Fund 10.09.2026,
     * arne.mov). Solange es gesetzt ist, aendert der Video-Wrapper keinen
     * Abspielzustand (kein play/pause, kein Sprung eines laufenden Videos);
     * gescrubbte (pausierte) Videos duerfen weiter springen, damit die Kachel
     * das richtige Bild zeigt.
     */
    static stillCapture = false;

    // Vorschaubild fuers Speichern: liefert den aktuellen Editor-Frame als
    // JPEG-Data-URL (oder null, wenn gerade nichts zu holen ist). Der Adapter
    // setzt den Hook (er kennt den Renderer); die Plattform-Seite ruft ihn beim
    // Speichern auf und laedt das Bild als Asset hoch. Reader: null.
    static captureEditorFrame: ((breite?: number, timerOverride?: number, restore?: boolean) => string | null) | null = null;
    /** Wie captureEditorFrame, aber MIT den HTML-Overlays (flache SVGs,
     *  Infografiken) — asynchron, weil das Overlay als Bild gerastert wird
     *  (Jans Kachel-Fund 12.10.2026; s. captureRendererFrameMitOverlays). */
    static captureEditorFrameMitOverlays:
        | ((breite?: number, timerOverride?: number, restore?: boolean) => Promise<string | null>)
        | null = null;
    /** Sind die Troika-Textebenen der Editor-Buehne fertig aufgebaut?
     *  (Kachel-Screenshots warten darauf, s. rendererTextBereit.) */
    static editorCaptureBereit: (() => boolean) | null = null;
    /** AUSRICHTEN an Kanten (Jan 24.10.2026): sichtbare Rahmen der Ebenen und
     *  die Bühnenkanten in Positionseinheiten — der Pro-Editor setzt den Hook
     *  (er kennt den Renderer, s. RendererWebGL.ausrichtRahmen). Reader: null. */
    static ausrichtRahmen:
        | ((ids: string[], timer: number) => {
              ebenen: Record<string, { links: number; rechts: number; oben: number; unten: number }>;
              buehne: { links: number; rechts: number; oben: number; unten: number } | null;
          } | null)
        | null = null;
}

